<template>
  <UiModalShell
    :show="props.showModal"
    title="Add Portal Admin"
    size="md"
    @close="closeModal"
  >
    <form id="portal-admin-modal-form" @submit.prevent="submitForm" class="space-y-5">
      <div
        v-if="error"
        class="p-3 bg-[rgba(240,82,82,0.08)] border border-[rgba(240,82,82,0.35)] rounded-[var(--radius-md)]"
      >
        <p class="text-err text-[13px]">{{ error }}</p>
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label class="block font-mono text-[10.5px] uppercase tracking-[0.14em] text-text-3 mb-1.5">
            First name <span class="text-err">*</span>
          </label>
          <UiInput v-model="first_name" placeholder="First name" />
        </div>
        <div>
          <label class="block font-mono text-[10.5px] uppercase tracking-[0.14em] text-text-3 mb-1.5">
            Last name <span class="text-err">*</span>
          </label>
          <UiInput v-model="last_name" placeholder="Last name" />
        </div>
        <div>
          <label class="block font-mono text-[10.5px] uppercase tracking-[0.14em] text-text-3 mb-1.5">
            Email <span class="text-err">*</span>
          </label>
          <UiInput v-model="email" type="email" placeholder="name@neutralfuels.com" />
        </div>
        <div>
          <label class="block font-mono text-[10.5px] uppercase tracking-[0.14em] text-text-3 mb-1.5">
            Contact number <span class="text-err">*</span>
          </label>
          <UiInput v-model="contact_number" type="tel" placeholder="+971 …" />
        </div>
      </div>

      <div class="p-3 rounded-[var(--radius-md)] bg-bg-2 border border-border">
        <p class="text-[12.5px] text-text-2">
          Portal super admins can view
          <span class="text-lime font-semibold">any activated myPortal customer</span>
          from a single account — no customer assignment needed. Internal use only.
        </p>
      </div>
    </form>

    <template #footer>
      <UiButton variant="ghost" :disabled="saving" @click="closeModal">Cancel</UiButton>
      <UiButton variant="primary" type="submit" form="portal-admin-modal-form" :loading="saving">
        Create portal admin
      </UiButton>
    </template>
  </UiModalShell>
</template>

<script setup>
import { ref, watch } from "vue";
import { useStore } from "vuex";
import UiModalShell from "@/components/ui/UiModalShell.vue";
import UiInput from "@/components/ui/UiInput.vue";
import UiButton from "@/components/ui/UiButton.vue";
import { AdminApiService } from "@/services/adminApi";

const props = defineProps({
  showModal: { type: Boolean, default: false },
});
const emit = defineEmits(["close", "created"]);

const store = useStore();
const adminApi = new AdminApiService(store.state.apiUrl);

const first_name = ref("");
const last_name = ref("");
const email = ref("");
const contact_number = ref("");
const saving = ref(false);
const error = ref(null);

const resetForm = () => {
  first_name.value = "";
  last_name.value = "";
  email.value = "";
  contact_number.value = "";
  error.value = null;
};

// Reset the form whenever the modal is (re-)opened, matching CustomerModal.
watch(
  () => props.showModal,
  (isOpen) => {
    if (isOpen) resetForm();
  }
);

const closeModal = () => {
  if (saving.value) return;
  resetForm();
  emit("close");
};

const submitForm = async () => {
  error.value = null;
  const trimmedFirstName = first_name.value.trim();
  const trimmedLastName = last_name.value.trim();
  const trimmedEmail = email.value.trim();
  const trimmedContactNumber = contact_number.value.trim();
  if (!trimmedFirstName || !trimmedLastName || !trimmedEmail || !trimmedContactNumber) {
    error.value = "Please fill in all required fields.";
    return;
  }
  saving.value = true;
  try {
    await adminApi.createAdminAccount({
      first_name: trimmedFirstName,
      last_name: trimmedLastName,
      email: trimmedEmail,
      customer_logo: "URL",
      contact_number: trimmedContactNumber,
      primary_account: "",
      role: "PORTAL_SUPER_ADMIN",
      account_type: "CUSTOMER",
      customers: [],
    });
    resetForm();
    emit("created");
  } catch (e) {
    error.value = e.message || "Failed to create portal admin.";
  } finally {
    saving.value = false;
  }
};
</script>
